/**
 * Axis geometry for a forest plot.
 *
 * Positions are computed on the analysis scale — natural logs for ratios, so a ratio
 * axis is logarithmic and 0.5 sits as far left of 1 as 2 sits right of it. Ratio ticks
 * come from the 1–2–5 ladder (…, 0.1, 0.2, 0.5, 1, 2, 5, 10, …), which is symmetric in
 * log space; additive ticks are "nice" steps of 1, 2, 2.5 or 5 × 10ᵐ. The line of no
 * effect (0, or 1 for a ratio) is always inside the axis.
 */

export interface ForestAxis {
  /** Domain on the analysis scale. */
  min: number;
  max: number;
  /** Tick positions on the analysis scale. */
  ticks: number[];
  /** The values printed at the ticks (ratios for a log axis). */
  labels: number[];
  /** The line of no effect, on the analysis scale. */
  reference: number;
}

const LADDER_STEPS = [1, 2, 5];
const MAX_RATIO_TICKS = 9;
const NICE_STEPS = [1, 2, 2.5, 5, 10];
/** Slack for values that land a rounding error away from a tick (exp(ln 0.5)). */
const TOLERANCE = 1e-9;

interface Rung {
  value: number;
  /** A power of ten — kept when a wide axis has to thin its ticks. */
  decade: boolean;
}

function ratioLadder(): Rung[] {
  const ladder: Rung[] = [];
  for (let power = -4; power <= 4; power++) {
    for (const step of LADDER_STEPS) {
      ladder.push({
        value: Number((step * 10 ** power).toPrecision(3)),
        decade: step === 1,
      });
    }
  }
  return ladder;
}

const LADDER = ratioLadder();

function ratioAxis(low: number, high: number): ForestAxis {
  const lo = Math.min(Math.exp(low), 1) * (1 + TOLERANCE);
  const hi = Math.max(Math.exp(high), 1) * (1 - TOLERANCE);
  const first =
    LADDER.filter((rung) => rung.value <= lo).at(-1)?.value ?? LADDER[0].value;
  const last =
    LADDER.find((rung) => rung.value >= hi)?.value ??
    (LADDER.at(-1) as Rung).value;
  let rungs = LADDER.filter(
    (rung) => rung.value >= first && rung.value <= last
  );
  if (rungs.length > MAX_RATIO_TICKS) {
    rungs = rungs.filter((rung) => rung.decade);
  }
  const labels = rungs.map((rung) => rung.value);
  return {
    min: Math.log(first),
    max: Math.log(last),
    ticks: labels.map(Math.log),
    labels,
    reference: 0,
  };
}

function niceStep(span: number): number {
  const raw = span / 5;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step =
    NICE_STEPS.find((candidate) => candidate * magnitude >= raw) ?? 10;
  return step * magnitude;
}

function additiveAxis(low: number, high: number): ForestAxis {
  const lo = Math.min(low, 0);
  const hi = Math.max(high, 0);
  const span = hi - lo || 1;
  const step = niceStep(span);
  const min = Math.floor(lo / step) * step;
  const max = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  for (let value = min; value <= max + step / 2; value += step) {
    ticks.push(Number(value.toPrecision(10)));
  }
  return { min, max, ticks, labels: ticks, reference: 0 };
}

/** Axis covering every interval and the line of no effect. */
export function forestAxis(
  lows: readonly number[],
  highs: readonly number[],
  ratio: boolean
): ForestAxis {
  const finiteLows = lows.filter(Number.isFinite);
  const finiteHighs = highs.filter(Number.isFinite);
  const low = Math.min(0, ...finiteLows);
  const high = Math.max(0, ...finiteHighs);
  return ratio ? ratioAxis(low, high) : additiveAxis(low, high);
}

/** Map an analysis-scale value into [left, right], clamped to the axis. */
export function axisPosition(
  value: number,
  axis: ForestAxis,
  left: number,
  right: number
): number {
  const clamped = Math.min(axis.max, Math.max(axis.min, value));
  return left + ((clamped - axis.min) / (axis.max - axis.min)) * (right - left);
}
